import { useState } from "react";

import { Box, Button } from "@chakra-ui/react";

import AddCustomerModal from "./AddCustomerModal";
import UploadFileModal from "./UploadFileModal";

import { Plus } from "lucide-react";
import { toaster } from "../../../../components/ui/toaster";
import { useMerchant } from "../../../../hooks/useMerchant";
import { CreateCustomer } from "../../../../types/types";
import { useEditCreateCustomer } from "../hooks/useEditCreateCustomer";
import { useMutateCustomers } from "../hooks/useMutateCustomers";

type Props = {
  refetch: () => void;
};

const Buttons = ({ refetch }: Props): JSX.Element => {
  const [isOpenAddCustomerModal, setIsOpenAddCustomerModal] = useState(false);
  const [isAddingCustomer, setIsAddingCustomer] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const { merchant } = useMerchant();

  const { customerBatch, getUploadUrl, resetBatch } =
    useMutateCustomers(refetch);

  ////////////////////////////// MANUAL CREATE //////////////////////////////
  async function handleCreateCustomer(
    customerData: CreateCustomer
  ): Promise<void> {
    setIsAddingCustomer(true);

    try {
      await createCustomerManually(customerData);
    } finally {
      setIsAddingCustomer(false);
    }
  }

  const createCustomerManually = useEditCreateCustomer(
    () => {
      toaster.create({
        title: "Cliente creado",
        description: "El cliente se ha creado con éxito.",
        type: "success",
        duration: 5000,
        meta: { closable: true },
      });
      refetch();
    },
    () => {
      toaster.create({
        title: "Error",
        description: "Hubo un error al crear el cliente.",
        type: "error",
        duration: 5000,
        meta: { closable: true },
      });
    }
  );

  ////////////////////////////// FILE UPLOAD //////////////////////////////

  const uploadFile = async (file: File): Promise<string> => {
    if (!merchant?.id) throw new Error("Merchant ID missing");

    const { uploadUrl, reference } = await getUploadUrl.mutateAsync({
      rentalsMerchantId: merchant.id,
    });

    const response = await fetch(uploadUrl, {
      method: "PUT",
      body: file,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Upload failed (${response.status}): ${errorText}`);
    }

    return reference;
  };

  return (
    <>
      <Box display={"flex"} gap={"1.5rem"}>
        <Button
          size="sm"
          variant={"outline"}
          hidden={customerBatch?.status !== "processing" || !customerBatch}
          onClick={() => setIsUploadModalOpen(true)}
        >
          Ver estado de la carga
        </Button>
        <Button
          size="sm"
          variant={"outline"}
          onClick={() => setIsUploadModalOpen(true)}
          hidden={customerBatch?.status === "processing"}
        >
          Carga masiva de clientes
        </Button>
        <Button
          size="sm"
          onClick={() => {
            setIsOpenAddCustomerModal(true);
          }}
          loading={isAddingCustomer}
          loadingText="Agregando cliente..."
        >
          <Plus size={16} />
          Agregar cliente
        </Button>
      </Box>

      <AddCustomerModal
        open={isOpenAddCustomerModal}
        onClose={() => {
          setIsOpenAddCustomerModal(false);
        }}
        onSubmit={handleCreateCustomer}
      />

      <UploadFileModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        customerBatch={customerBatch}
        uploadFile={uploadFile}
        resetBatch={resetBatch}
      />
    </>
  );
};

export default Buttons;
